import { Stack, Text, Box, Switch, FormHelperText } from '@chakra-ui/react';
import { Field } from 'formik';
import { Select as NumberSelect } from 'chakra-react-select';
import { FormControl, FormLabel, Input } from '@/components/v4';
import NumberField from 'components/forms/new/formikFormFields/NumberField';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
    dropDownFormStyle,
} from 'components/forms/new/formStyles';
import { addSpacing } from '@/utils/utils';
import {
    EXTENDED_ATTRIBUTE_TYPE,
    FIELD_WIDTH,
    fieldInputStyling,
    getRangeHelperText,
    getListOptions,
    isMaskedAttribute,
} from './RingGroupExtendedAttributes.constants';

// Same dropDownFormStyle as Site/Primary Number, just pinned to the shared field width too.
const getListSelectStyles = (darkMode) => {
    const base = dropDownFormStyle(darkMode);
    return {
        ...base,
        container: (provided, state) => ({
            ...base.container(provided, state),
            width: FIELD_WIDTH,
        }),
    };
};

// Only masked and INT/NUMERIC fields render helper text; shared so row spacing can compensate for rows without one.
const getHelperTextForAttribute = (attribute) => {
    if (isMaskedAttribute(attribute.attributeName, attribute.attributeType)) {
        return getRangeHelperText(attribute, 'digits');
    }
    if (
        attribute.attributeType === EXTENDED_ATTRIBUTE_TYPE.INT ||
        attribute.attributeType === EXTENDED_ATTRIBUTE_TYPE.NUMERIC
    ) {
        return getRangeHelperText(attribute);
    }
    return null;
};

const renderField = (attribute, fieldName, darkMode) => {
    if (isMaskedAttribute(attribute.attributeName, attribute.attributeType)) {
        const helperText = getHelperTextForAttribute(attribute);
        return (
            <Field name={fieldName}>
                {({ field, form }) => (
                    <Box flex="1" pt="15px">
                        <Input
                            {...field}
                            value={field?.value ?? ''}
                            type="password"
                            inputMode="numeric"
                            maxLength={attribute.maximum ?? undefined}
                            onChange={(e) =>
                                form.setFieldValue(
                                    fieldName,
                                    e.target.value.replace(/\D/g, ''),
                                )
                            }
                            sx={fieldInputStyling}
                        />
                        {helperText && (
                            <FormHelperText>{helperText}</FormHelperText>
                        )}
                    </Box>
                )}
            </Field>
        );
    }

    switch (attribute.attributeType) {
        case EXTENDED_ATTRIBUTE_TYPE.BOOLEAN:
            return (
                <Field name={fieldName}>
                    {({ field, form }) => (
                        <Box flex="1" pt="15px">
                            <Switch
                                isChecked={String(field.value) === 'true'}
                                onChange={(e) =>
                                    form.setFieldValue(
                                        fieldName,
                                        e.target.checked ? 'true' : 'false',
                                    )
                                }
                            />
                        </Box>
                    )}
                </Field>
            );
        case EXTENDED_ATTRIBUTE_TYPE.LIST: {
            const options = getListOptions(attribute);
            return (
                <Field name={fieldName}>
                    {({ field, form }) => (
                        <Box flex="1" pt="15px">
                            <NumberSelect
                                options={options}
                                value={
                                    options.find(
                                        (o) => o.value === field.value,
                                    ) || null
                                }
                                onChange={(option) =>
                                    form.setFieldValue(
                                        fieldName,
                                        option?.value || '',
                                    )
                                }
                                chakraStyles={getListSelectStyles(darkMode)}
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}
                            />
                        </Box>
                    )}
                </Field>
            );
        }
        case EXTENDED_ATTRIBUTE_TYPE.INT:
        case EXTENDED_ATTRIBUTE_TYPE.NUMERIC: {
            const helperText = getHelperTextForAttribute(attribute);
            return (
                <Field name={fieldName}>
                    {({ field }) => (
                        // NumberField supplies its own top padding — no extra pt here, or it double-applies.
                        <Box flex="1">
                            <NumberField
                                field={field}
                                step={
                                    attribute.attributeType ===
                                    EXTENDED_ATTRIBUTE_TYPE.INT
                                        ? '1'
                                        : 'any'
                                }
                                min={attribute.minimum ?? undefined}
                                max={attribute.maximum ?? undefined}
                                width={FIELD_WIDTH}
                                customWidth={FIELD_WIDTH}
                            />
                            {helperText && (
                                <FormHelperText>{helperText}</FormHelperText>
                            )}
                        </Box>
                    )}
                </Field>
            );
        }
        // TEXT and GUID — plain free-text input, no extra constraints today.
        default:
            return (
                <Field name={fieldName}>
                    {({ field }) => (
                        <Box flex="1" pt="15px">
                            <Input {...field} sx={fieldInputStyling} />
                        </Box>
                    )}
                </Field>
            );
    }
};

export default function RingGroupExtendedAttributes({
    attributes = [],
    darkMode,
}) {
    const activeAttributes = attributes.filter(
        (attribute) => attribute?.isActive !== false,
    );

    if (activeAttributes.length === 0) {
        return (
            <Text fontSize="sm" color="gray.500">
                No extended attributes configured
            </Text>
        );
    }

    return (
        <Stack spacing={1}>
            {activeAttributes.map((attribute) => {
                const fieldName = `extendedAttributes.${attribute.attributeName}`;
                return (
                    <FormControl
                        key={attribute.attributeName}
                        sx={{ margin: '0 !important' }}>
                        <Stack
                            direction="row"
                            spacing={stackSpacing}
                            sx={stackStyling()}>
                            <FormLabel sx={formLabelStyling(darkMode)}>
                                {addSpacing(attribute.attributeName) ||
                                    attribute.attributeName}{' '}
                                {attribute.required && (
                                    <span style={{ color: '#e53e3e' }}>*</span>
                                )}
                            </FormLabel>
                            {renderField(attribute, fieldName, darkMode)}
                        </Stack>
                    </FormControl>
                );
            })}
        </Stack>
    );
}
